import { useSetAtom } from 'jotai';
import { useCallback } from 'react';

import { useIntercom } from '@/hooks/useIntercom';
import { useNavigateAwayFromFormEditor } from '@/hooks/useNavigateAwayFromFormEditor';
import { track } from '@/lib/analytics/track';
import { PDF_NON_FILLABLE_MAX_PAGES } from '@/lib/fileUploadLimits';
import { ReactPdfDocument, ReactPdfPage } from '@/lib/pdf/react-pdf-loader';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { ErrorSeverity, ErrorSurface } from '@/models/analytics/features/errors';
import { isHeidiSupportReachable } from '@/utils/heidiReach';
import { getPdfPageDimensions } from '@/utils/pdf-fields';
import type { FormFieldEditorPorts } from '@heidi/templates';
import { xmlFormReviewFileAtom, xmlFormReviewModalOpenAtom } from '@heidi/templates/data';

// The door publishes `FormFieldEditorPorts`; the shapes below are read off it
// rather than named separately, so the binding cannot drift from the port and
// the public surface stays the names an importer actually asks for.
type FormFieldEditorTrackedEvent = Parameters<FormFieldEditorPorts['trackEvent']>[0];
type EditorErrorEvent = Extract<
  FormFieldEditorTrackedEvent,
  { event_name: 'error_viewed' }
>;
type FormFieldEditorErrorSurface = EditorErrorEvent['props']['surface'];
type FormFieldEditorErrorSeverity = EditorErrorEvent['props']['severity'];

/**
 * `TrackingEventName` is a string enum, so the editor's four event names bind to
 * MEMBERS here rather than being cast at the call site. The map is total over
 * the port's union, so a name added to the port with no member fails here.
 */
const EDITOR_EVENT_NAMES = {
  form_template_fields_updated: TrackingEventName.FormTemplateFieldsUpdated,
  button_clicked: TrackingEventName.ButtonClicked,
  modal_viewed: TrackingEventName.ModalViewed,
  error_viewed: TrackingEventName.ErrorViewed,
} satisfies Record<FormFieldEditorTrackedEvent['event_name'], TrackingEventName>;

const EDITOR_ERROR_SURFACES = {
  toast: ErrorSurface.TOAST,
  banner: ErrorSurface.BANNER,
  modal: ErrorSurface.MODAL,
  inline: ErrorSurface.INLINE,
} satisfies Record<FormFieldEditorErrorSurface, ErrorSurface>;

const EDITOR_ERROR_SEVERITIES = {
  critical: ErrorSeverity.CRITICAL,
  warning: ErrorSeverity.WARNING,
  info: ErrorSeverity.INFO,
} satisfies Record<FormFieldEditorErrorSeverity, ErrorSeverity>;

const trackEditorEvent = (event: FormFieldEditorTrackedEvent): void => {
  switch (event.event_name) {
    case 'form_template_fields_updated':
      track({
        event_name: EDITOR_EVENT_NAMES.form_template_fields_updated,
        props: event.props,
        options: event.options,
      });
      return;
    case 'button_clicked':
      track({
        event_name: EDITOR_EVENT_NAMES.button_clicked,
        props: event.props,
        options: event.options,
      });
      return;
    case 'modal_viewed':
      track({
        event_name: EDITOR_EVENT_NAMES.modal_viewed,
        props: event.props,
        options: event.options,
      });
      return;
    case 'error_viewed':
      track({
        event_name: EDITOR_EVENT_NAMES.error_viewed,
        props: {
          ...event.props,
          surface: EDITOR_ERROR_SURFACES[event.props.surface],
          severity: EDITOR_ERROR_SEVERITIES[event.props.severity],
        },
        options: event.options,
      });
  }
};

// Thin adapters rather than the loader's components directly: react-pdf's own
// prop types carry `propTypes` validators whose variance makes them
// unassignable to the narrower shape the editor declares.
const PdfDocumentPort: FormFieldEditorPorts['PdfDocument'] = ({ children, ...props }) => (
  <ReactPdfDocument {...props}>{children}</ReactPdfDocument>
);

const PdfPagePort: FormFieldEditorPorts['PdfPage'] = (props) => (
  <ReactPdfPage {...props} />
);

const useOpenXmlFormReview: FormFieldEditorPorts['useOpenXmlFormReview'] = () => {
  const setXmlFormReviewFile = useSetAtom(xmlFormReviewFileAtom);
  const setXmlFormReviewModalOpen = useSetAtom(xmlFormReviewModalOpenAtom);
  const navigateAwayFromFormEditor = useNavigateAwayFromFormEditor();

  return useCallback(
    (file: File) => {
      setXmlFormReviewFile(file);
      setXmlFormReviewModalOpen(true);
      navigateAwayFromFormEditor('replace');
    },
    [setXmlFormReviewFile, setXmlFormReviewModalOpen, navigateAwayFromFormEditor]
  );
};

/**
 * Module-scope so every mount hands the editor the same object identity and the
 * context value it feeds does not change on each render.
 */
export const FORM_FIELD_EDITOR_PORTS = {
  PdfDocument: PdfDocumentPort,
  PdfPage: PdfPagePort,
  getPageDimensions: getPdfPageDimensions,
  trackEvent: trackEditorEvent,
  useContactSupport: () => useIntercom().showMessageModal,
  useOpenXmlFormReview,
  isSupportReachable: isHeidiSupportReachable,
  nonFillableMaxPages: PDF_NON_FILLABLE_MAX_PAGES,
} satisfies FormFieldEditorPorts;
